import React, { useState, useEffect, useCallback } from 'react';
import { Search, Gift, Package, RefreshCcw } from 'lucide-react';
import { useAlert } from '../../../context/AlertContext';
import Layout from '../../../components/Layout';
import API_BASE_URL, { getAuthHeaders, getAuthToken } from '../../../config/api';

const BogoManagement = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [savingId, setSavingId] = useState(null);
    const { showAlert } = useAlert();

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = getAuthToken();
            const response = await fetch(`${API_BASE_URL}/admin/products.php?_cb=${Date.now()}${token ? `&token=${token}` : ''}`, {
                headers: getAuthHeaders()
            });
            const data = await response.json();
            if (data.success) {
                setProducts(data.products);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            showAlert('error', 'Failed to fetch products');
        } finally {
            setLoading(false);
        }
    }, [showAlert]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleBogoChange = (productId, field, value) => {
        setProducts(prev => prev.map(p =>
            p.id === productId ? { ...p, [field]: value } : p
        ));
    };

    const saveBogo = async (product) => {
        setSavingId(product.id);
        try {
            const token = getAuthToken();
            const response = await fetch(`${API_BASE_URL}/admin/products.php${token ? `?token=${token}` : ''}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    id: product.id,
                    product_name: product.product_name,
                    category_id: product.category_id,
                    description: product.description,
                    price: product.price,
                    pv_points: product.pv_points,
                    image_url: product.image_url,
                    stock_quantity: product.stock_quantity,
                    buy_quantity: product.buy_quantity,
                    get_free_quantity: product.get_free_quantity,
                    free_product_id: product.free_product_id,
                    status: product.status
                })
            });

            const data = await response.json();
            if (data.success) {
                showAlert('success', `BOGO updated for ${product.product_name}`);
            } else {
                showAlert('error', data.message || 'Update failed');
            }
        } catch (error) {
            console.error('Error saving BOGO:', error);
            showAlert('error', 'An error occurred while saving');
        } finally {
            setSavingId(null);
        }
    };

    const filteredProducts = products.filter(p =>
        p.product_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Layout>
            <div className="p-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-black text-gray-900 flex items-center gap-3">
                            <Gift className="text-amber-500" size={32} />
                            BOGO Offer Management
                        </h1>
                        <p className="text-gray-500 font-medium tracking-tight mt-1">Configure Buy-One-Get-One offers for your store products.</p>
                    </div>
                    <button
                        onClick={fetchData}
                        className="p-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
                        title="Refresh List"
                    >
                        <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
                    </button>
                </div>

                {/* Search */}
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 mb-6 max-w-md">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search products to set offers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all font-medium"
                        />
                    </div>
                </div>

                {/* Desktop Table / Mobile Cards */}
                <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50/50 border-b border-gray-100">
                                <tr>
                                    <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Product Info</th>
                                    <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Buy Qty</th>
                                    <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Get Free</th>
                                    <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Free Product</th>
                                    <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {loading && products.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-20 text-center">
                                            <div className="flex flex-col items-center gap-3 opacity-30">
                                                <RefreshCcw size={48} className="animate-spin" />
                                                <p className="font-bold uppercase tracking-widest text-xs">Loading Inventory...</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredProducts.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-20 text-center text-gray-400">
                                            No products match your search.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredProducts.map(product => (
                                        <tr key={product.id} className="hover:bg-gray-50/50 transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center overflow-hidden border border-gray-200 shadow-inner">
                                                        {product.image_url ? (
                                                            <img src={product.image_url} alt="" className="w-full h-full object-cover" />
                                                        ) : (
                                                            <Package size={20} className="text-gray-300" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{product.product_name}</p>
                                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">₹{parseFloat(product.price).toLocaleString()}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <input
                                                    type="number"
                                                    value={product.buy_quantity}
                                                    onChange={(e) => handleBogoChange(product.id, 'buy_quantity', e.target.value)}
                                                    min="0"
                                                    className="w-20 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:bg-blue-50 focus:border-blue-400 outline-none text-center font-bold"
                                                />
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <input
                                                    type="number"
                                                    value={product.get_free_quantity}
                                                    onChange={(e) => handleBogoChange(product.id, 'get_free_quantity', e.target.value)}
                                                    min="0"
                                                    className="w-20 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:bg-amber-50 focus:border-amber-400 outline-none text-center font-bold"
                                                />
                                            </td>
                                            <td className="px-6 py-4">
                                                <select
                                                    value={product.free_product_id || ''}
                                                    onChange={(e) => handleBogoChange(product.id, 'free_product_id', e.target.value)}
                                                    className="w-full max-w-[200px] px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-4 focus:ring-blue-100 outline-none text-sm font-medium"
                                                >
                                                    <option value="">(Same Product)</option>
                                                    {products.filter(p => p.id !== product.id).map(p => (
                                                        <option key={p.id} value={p.id}>{p.product_name}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <button
                                                    onClick={() => saveBogo(product)}
                                                    disabled={savingId === product.id}
                                                    className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${savingId === product.id
                                                        ? 'bg-gray-100 text-gray-400'
                                                        : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-100 hover:shadow-blue-200'
                                                        }`}
                                                >
                                                    {savingId === product.id ? 'Saving...' : 'Save Offer'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="mt-8 bg-amber-50 border border-amber-100 p-6 rounded-[2rem] flex items-start gap-4">
                    <div className="p-3 bg-amber-500 rounded-2xl text-white shadow-lg shadow-amber-200">
                        <Gift size={24} />
                    </div>
                    <div>
                        <h4 className="font-black text-amber-900 uppercase tracking-tight text-lg">BOGO Tips</h4>
                        <div className="mt-2 space-y-1 text-sm text-amber-800 font-medium">
                            <p>• To set <strong>"Buy 1 Get 1 Free"</strong>: Set Buy Qty = 1 and Get Free = 1.</p>
                            <p>• To set <strong>"Buy 3 Get 1 Free"</strong>: Set Buy Qty = 3 and Get Free = 1.</p>
                            <p>• Leaving "Free Product" as <strong>(Same Product)</strong> will give the same item for free.</p>
                            <p>• Set both quantities to <strong>0</strong> to remove the offer from a product.</p>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default BogoManagement;
